import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { 
  LayoutDashboard, 
  Building, 
  Users, 
  UsersRound, 
  Shield, 
  Boxes, 
  CreditCard, 
  Settings,
  LogOut,
  ChevronDown,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { HumaTwinIcon } from '../components/brand/HumaTwinLogo';

export default function EnterpriseLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { orgId } = useParams();
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (orgId && orgs.length > 0) {
      const org = orgs.find(o => o.id === orgId);
      setCurrentOrg(org);
    }
  }, [orgId, orgs]);

  const fetchOrganizations = async () => {
    try {
      const response = await apiClient.get('/organizations/');
      setOrgs(response.data);
      if (response.data.length > 0 && !orgId) {
        setCurrentOrg(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const navItems = [
    { label: 'Dashboard', path: `/enterprise/orgs/${orgId}/dashboard`, icon: LayoutDashboard },
    { label: 'Analytics', path: `/enterprise/orgs/${orgId}/analytics`, icon: BarChart3 },
    { label: 'Organization', path: `/enterprise/orgs/${orgId}/profile`, icon: Building },
    { label: 'People', path: `/enterprise/orgs/${orgId}/people`, icon: Users },
    { label: 'Teams', path: `/enterprise/orgs/${orgId}/teams`, icon: UsersRound },
    { label: 'Access Control', path: `/enterprise/orgs/${orgId}/access`, icon: Shield },
    { label: 'Apps & Tools', path: `/enterprise/orgs/${orgId}/apps`, icon: Boxes },
    { label: 'Billing', path: `/enterprise/orgs/${orgId}/billing`, icon: CreditCard },
    { label: 'Settings', path: `/enterprise/orgs/${orgId}/settings`, icon: Settings },
  ];

  return (
    <div className="min-h-screen flex bg-gray-50 enterprise-selection">
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <HumaTwinIcon size={32} />
            <div>
              <h1 className="font-heading text-2xl font-bold text-enterprise-slate">HumaTwin</h1>
              <p className="text-sm text-gray-500">Enterprise</p>
            </div>
          </div>
        </div>

        <div className="p-4 border-b border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between"
                data-testid="org-switcher"
              >
                <span className="truncate">{currentOrg?.name || 'Select Organization'}</span>
                <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              {orgs.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => {
                    setCurrentOrg(org);
                    window.location.href = `/enterprise/orgs/${org.id}/dashboard`;
                  }}
                >
                  {org.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-enterprise-slate/10 text-enterprise-slate font-medium'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <Avatar>
              <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={logout}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
